import { Text, IconConfigureLine, Drilldown, Pill, View } from '@smarterservices/smarter-ui';
import { capitalizeFirstLetter } from '@instructure/ui-utils';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { getStore, usePlayerStore } from '../../../../stores/StoreContext';
import { useFullScreenMountNode } from '../../../../contexts/FullScreenContext';
import CONSTANTS from '../../../../config/constants';
import NavigationalMenuItem from '@ui/shared/components/Common/NavigationalMenuItem';
import TriggerButton from '@ui/shared/components/Common/TriggerButton';

const AnnotationOptions = () => {
  const PlayerStore = getStore('playerStore');
  const SettingsStore = getStore('settingsStore');
  const mountNode = useFullScreenMountNode();

  {
    /* Listen to any changes in the stores... */
  }
  const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
  const displayMarkers = usePlayerStore((state) => state.displayMarkers);
  const displayVideoLabels = usePlayerStore((state) => state.displayVideoLabels);
  const smartSpeedEnabled = usePlayerStore((state) => state.smartSpeedEnabled);
  const smartSpeedName = usePlayerStore((state) => state.smartSpeedName);
  const viewRawHlsVideo = usePlayerStore((state) => state.viewRawHlsVideo);

  const [markerBulkUpdateLabel, setMarkerBulkUpdateLabel] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (!bulkUpdating) {
      const allSelected = PlayerStore.markers.length === displayMarkers.length;
      setMarkerBulkUpdateLabel(allSelected ? 'Disable All' : 'Enable All');
    }
  }, [displayMarkers, bulkUpdating]);

  const bulkUpdateSelectedAnnotations = async (event) => {
    setBulkUpdating(true);
    try {
      if (PlayerStore.markers.length === displayMarkers.length) {
        setMarkerBulkUpdateLabel('Disabling...');
        const togglePromises = PlayerStore.markers
          .filter(marker => displayMarkers.includes(marker.id) && marker.id !== 'flags')
          .map(marker => PlayerStore.toggleMarker(marker.id));
        await Promise.all(togglePromises);
        setMarkerBulkUpdateLabel('Enable All');
      } else {
        setMarkerBulkUpdateLabel('Enabling...');
        const togglePromises = PlayerStore.markers
          .filter(marker => !displayMarkers.includes(marker.id))
          .map(marker => PlayerStore.toggleMarker(marker.id));
        await Promise.all(togglePromises);
        setMarkerBulkUpdateLabel('Disable All');
      }
    } catch (error) {
      console.error('Error during bulk marker update:', error);
      setMarkerBulkUpdateLabel(
        PlayerStore.markers.length === displayMarkers.length ? 'Disable All' : 'Enable All'
      );
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleAnnotationSelect = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  const handleSpeedSelect = useCallback((value) => {
    PlayerStore.setPlaybackSpeed(value);
  });

  const handleVideoLabelsSelect = useCallback((value) => {
    PlayerStore.setDisplayVideoLabels(value);
  });

  const handleRecordingViewerSelect = useCallback((value) => {
    PlayerStore.setViewRawHlsVideo(value === 'raw');
  });

  const handleAudioSettingsClick = useCallback(() => {
    SettingsStore.openSettingsModal('audio');
    setIsMenuOpen(false);
  });

  const annotationOptions = useMemo(() => {
    const excludeMarkers = ['flags', 'events'];
    return PlayerStore.markers
      .filter(marker => !excludeMarkers.includes(marker.id))
      .map(marker => (
        <Drilldown.Option
          onSelect={handleAnnotationSelect}
          id={marker.id}
          selected={displayMarkers.includes(marker.id)}
          key={`marker_${marker.id}`}
          value={marker.id}
          onOptionClick={async () => await PlayerStore.toggleMarker(marker.id)}
        >
          <NavigationalMenuItem
            shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.[marker.label.toUpperCase()]?.KEY}
          >
            {marker.label}
          </NavigationalMenuItem>
        </Drilldown.Option>
      ));
  }, [PlayerStore.markers, displayMarkers]);

  const renderAnnotationOptionGroup = () => {
    return (
      <Drilldown.Group
        id={`annotations_group`}
        selectableType="multiple"
        onSelect={handleAnnotationSelect}
      >
        {annotationOptions}
      </Drilldown.Group>
    );
  };

  const speedOptions = useMemo(() => {
    const _playbackSpeed = smartSpeedEnabled ? '100.0' : playbackSpeed;
    return PlayerStore.speedOptions.map(speed => (
      <Drilldown.Option
        id={`${speed.value}`}
        selected={_playbackSpeed === PlayerStore.formatSpeedValue(speed.value)}
        key={`speed_${speed.key}`}
        value={speed.value}
        onOptionClick={() => handleSpeedSelect(speed.value)}
      >
        <NavigationalMenuItem
          shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.[speed.key.toUpperCase()]?.KEY}
        >
          {speed.label}
        </NavigationalMenuItem>
      </Drilldown.Option>
    ));
  }, [playbackSpeed, smartSpeedEnabled, PlayerStore.speedOptions]);

  const renderSpeedOptionGroup = () => {
    return (
      <Drilldown.Group id={`speed_group`} selectableType="single">
        {speedOptions}
      </Drilldown.Group>
    );
  };

  const videoLabelOptions = useMemo(() => [
    <Drilldown.Option
      id="show_video_labels"
      key="show_video_labels"
      selected={displayVideoLabels === "show"}
      value="show"
      onOptionClick={() => handleVideoLabelsSelect("show")}
    >
      <NavigationalMenuItem
        shortcut={CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.VIDEO_LABELS.SHOW.KEY}
      >
        Show
      </NavigationalMenuItem>
    </Drilldown.Option>,
    <Drilldown.Option
      id="hide_video_labels"
      key="hide_video_labels"
      selected={displayVideoLabels === "hide"}
      value="hide"
      onOptionClick={() => handleVideoLabelsSelect("hide")}
    >
      <NavigationalMenuItem
        shortcut={CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.VIDEO_LABELS.HIDE.KEY}
      >
        Hide
      </NavigationalMenuItem>
    </Drilldown.Option>
  ], [displayVideoLabels]);

  const renderVideoLabelsOptionGroup = () => {
    return (
      <Drilldown.Group id={`video_labels_group`} selectableType="single">
        {videoLabelOptions}
      </Drilldown.Group>
    );
  };

  const recordingViewerOptions = useMemo(() => [
    <Drilldown.Option
      id="integrated_viewer"
      key="integrated_viewer"
      selected={!viewRawHlsVideo}
      value="integrated"
      onOptionClick={() => handleRecordingViewerSelect("integrated")}
    >
      <NavigationalMenuItem>
        Integrated
      </NavigationalMenuItem>
    </Drilldown.Option>,
    <Drilldown.Option
      id="raw_viewer"
      key="raw_viewer"
      selected={viewRawHlsVideo}
      value="raw"
      onOptionClick={() => handleRecordingViewerSelect("raw")}
    >
      <NavigationalMenuItem>
        Grid
      </NavigationalMenuItem>
    </Drilldown.Option>
  ], [viewRawHlsVideo]);

  const renderRecordingViewerOptionGroup = () => {
    return (
      <Drilldown.Group id={`recording_viewer_group`} selectableType="single">
        {recordingViewerOptions}
      </Drilldown.Group>
    );
  };

  const renderSelected = (props, value) => {
    return (
      <Pill size="small">
        {value}
      </Pill>
    );
  };

  return (
    <Drilldown
      rootPageId="videoSettings"
      width="18.5rem"
      maxHeight="26.5rem"
      shouldHideOnSelect={false}
      mountNode={() => mountNode}
      trigger={
        <TriggerButton
          size="small"
          margin="none none none x-small"
          screenReaderLabel="View session viewer settings"
        >
          <IconConfigureLine />
        </TriggerButton>
      }
      show={isMenuOpen}
      onToggle={(_event, { shown, goToPage }) => {
        setIsMenuOpen(shown);
        shown && goToPage('videoSettings');
      }}
    >
      <Drilldown.Page
        id="videoSettings"
        renderTitle={() => (
          <View as="div" borderColor="primary-inverse" borderWidth='0 0 small 0' padding="0 0 small 0">
            <Text weight="bold" color="brand">
              Session Viewer Settings
            </Text>
          </View>
        )}
        withoutHeaderSeparator
      >
        <Drilldown.Option
          id="Annotations"
          subPageId="annotations"
          renderLabelInfo={(props) => {
            // since flags are always displayed, if that is the only one don't show any count...
            if (displayMarkers?.length === 1) {
              return null;
            }
            return renderSelected(props, `${displayMarkers?.length - 1} selected`)
          }}
        >
          Markers
        </Drilldown.Option>

        <Drilldown.Option
          id="videoLabels"
          subPageId="videoLabels"
          renderLabelInfo={(props) => renderSelected(props, capitalizeFirstLetter(displayVideoLabels))}
        >
          Video Labels
        </Drilldown.Option>
        <Drilldown.Option
          id="recordingViewer"
          subPageId="recordingViewer"
          renderLabelInfo={(props) => renderSelected(props, viewRawHlsVideo ? 'Grid' : 'Integrated')}
        >
          Playback Layout
        </Drilldown.Option>
        <Drilldown.Option
          id="speed"
          subPageId="speed"
          renderLabelInfo={(props) => {
            if (smartSpeedEnabled) {
              return renderSelected(props, smartSpeedName);
            } else if (playbackSpeed === '1.0' || playbackSpeed === '1' || playbackSpeed === 1) {
              return renderSelected(props, 'Normal');
            } else {
              return renderSelected(props, playbackSpeed);
            }
          }}
        >
          Speed
        </Drilldown.Option>
        <Drilldown.Option
          id="audioSettings"
          onOptionClick={handleAudioSettingsClick}
        >
          Audio Settings
        </Drilldown.Option>
      </Drilldown.Page>

      <Drilldown.Page
        id="annotations"
        renderTitle="Markers"
        renderActionLabel={markerBulkUpdateLabel}
        onHeaderActionClicked={async () =>
          await bulkUpdateSelectedAnnotations()
        }
      >
        {renderAnnotationOptionGroup()}
      </Drilldown.Page>

      <Drilldown.Page id="speed" renderTitle="Speed">
        {renderSpeedOptionGroup()}
      </Drilldown.Page>

      <Drilldown.Page id="videoLabels" renderTitle="Video Labels">
        {renderVideoLabelsOptionGroup()}
      </Drilldown.Page>

      <Drilldown.Page id="recordingViewer" renderTitle="Playback Layout">
        {renderRecordingViewerOptionGroup()}
      </Drilldown.Page>
    </Drilldown>
  );
};

export default AnnotationOptions;
